<template>
  <div
    class="h-16 p-6 flex items-center justify-between bg-white laptop:rounded-xl shadow-sm sticky top-6 z-50 w-full"
  >
    <template v-if="isSmallScreen">
      <div class="flex items-center justify-between w-full h-full">
        <div
          class="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center text-white text-xs flex-shrink-0"
        >
          <img
            :key="userStore.imageVersion"
            :src="userStore.profileImage"
            alt="Profile"
            class="rounded-full w-full h-full object-cover"
            @error="onErrorImage"
          />
        </div>

        <div
          class="flex-1 flex items-center justify-center relative h-full mx-2"
        >
          <div
            v-show="!showSearchInput"
            class="absolute inset-0 flex justify-center items-center gap-2 transition-opacity duration-300 ease-in-out"
          >
            <div class="flex-1 flex justify-center items-center h-6">
              <div class="flex items-center gap-2">
                <div v-html="sidebarLved" v-show="isAbove426" class="h-full w-auto z-50 flex-shrink-0 max-w-[140px]"></div>
              </div>
            </div>
          </div>
        </div>

        <div class="flex items-center gap-4 flex-shrink-0">
          <LanguageDropdown class="hidden sm:block" />
          <button
            type="button"
            @click="toggleSearchInput"
            class="cursor-pointer !bg-transparent p-0 border-none"
            :aria-label="$t('accessibilityLabels.toggleSearch')"
          >
            <div
              v-if="showSearchInput"
              v-html="closeSvg({ width: '24px', height: '24px' })"
              class="h-6 w-6 flex items-center"
            ></div>
          </button>
          <div class="relative font-just">
            <button
              type="button"
              @click="toggleNotificationMenu"
              class="cursor-pointer !bg-transparent p-0 border-none"
              :aria-label="$t('accessibilityLabels.openNotifications')"
            >
              <span
                v-html="notificationIcon({ color: '#494949' })"
                class="h-6 w-6"
              ></span>
              <span
                v-if="notificationStore.newNotificationsCount > 0"
                class="absolute -top-2 -right-2 bg-notificationBadge text-white text-[12px] font-[500] leading-[100%] tracking-[0%] rounded-full px-1.5 py-0.5 flex items-center justify-center min-w-[20px] min-h-[20px] border-2 border-white"
              >
                {{ notificationStore.newNotificationsCount }}
              </span>
            </button>
            <Menu
              ref="notificationMenu"
              :model="notificationMenuItems"
              :popup="true"
              class="notification-menu min-w-[320px] max-w-[360px]"
              :pt="{
                list: {
                  class: `scroll-thin overflow-y-auto ${
                    notificationMenuItems.length == 1 &&
                    notificationMenuItems[0]?.id === 'no-data'
                      ? 'max-h-[65px]'
                      : 'max-h-[300px]'
                  }`,
                },
                item: { class: 'gap-3 flex' },
                itemContent: { class: 'p-3 gap-3 flex' },
                separator: { class: 'block mx-3 bg-grayLight' },
                root: { class: '!rounded-b-2xl flex bg-white !gap-3' },
              }"
              style="
                box-shadow: -4px 11px 26px 0px #0000001a;
                box-shadow: -16px 43px 46px 0px #00000017;
                box-shadow: -37px 98px 63px 0px #0000000d;
                box-shadow: -65px 174px 74px 0px #00000003;
                box-shadow: -102px 272px 81px 0px #00000000;
              "
            >
              <template #item="slotProps">
                <div
                  class="cursor-pointer flex items-center justify-center gap-3"
                  @click="handleNotificationClick(slotProps.item, $event)"
                >
                  <div v-if="slotProps.item.id !== 'no-data'">
                    <span
                      v-if="slotProps.item.id % 2 !== 0"
                      class="rounded-full bg-deepSky w-8 h-8 min-w-8 min-h-8 flex items-center justify-center"
                      v-html="requestArrowSvg()"
                    ></span>
                    <span
                      v-else
                      class="rounded-full bg-greenSuccess w-8 h-8 min-w-8 min-h-8 flex items-center justify-center"
                      v-html="notificationCheckSvg()"
                    ></span>
                  </div>
                  <div
                    class="flex-1 font-just py-1 gap-2 flex items-start flex-col justify-start"
                  >
                    <div
                      class="text-btnActive text-[16px] font-semibold leading-[100%] tracking-[0%]"
                    >
                      {{ slotProps.item.label }}
                    </div>
                    <div
                      v-if="slotProps.item.id !== 'no-data'"
                      class="text-btnActive text-[14px] font-normal leading-[100%] tracking-[0%]"
                    >
                      {{ slotProps.item.description }}
                    </div>
                  </div>
                  <div v-if="slotProps.item.id !== 'no-data'">
                    <span
                      v-html="
                        rightArrow({ width: 24, height: 24, color: '#494949' })
                      "
                      class=""
                    ></span>
                  </div>
                </div>
              </template>
            </Menu>
          </div>
          <button
            ref="rightPanelToggle"
            type="button"
            @click="toggleRightPanel"
            @keydown.enter.prevent="toggleRightPanel"
            class="cursor-pointer !bg-transparent p-0 border-none focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-deepSky"
            :aria-label="$t('accessibilityLabels.toggleRightPanel')"
            :aria-expanded="rightPanelStore.isRightPanelVisible"
          >
            <div
              v-html="menuIcon"
              class="h-6 w-6 cursor-pointer pointer-events-none"
            ></div>
          </button>
        </div>
      </div>
    </template>
    <template v-else>
      <div class="flex items-center gap-4">
        <div v-if="!isSmallScreen">
          <button
            type="button"
            @click="sidebarStore.toggleSidebar"
            class="cursor-pointer !bg-transparent p-0 border-none"
            :aria-label="$t('accessibilityLabels.toggleSidebar')"
          >
            <div
              v-html="sidebarLeftToggle"
              class="h-6 w-6 cursor-pointer"
            ></div>
          </button>
        </div>

        <div class="w-[1px] bg-gray-300 h-6"></div>
        <div v-if="!isDashboard && canGoBack">
          <button
            type="button"
            @click="goToPreviousPage"
            class="cursor-pointer !bg-transparent p-0 border-none"
            :aria-label="$t('accessibilityLabels.goToPreviousPage')"
          >
            <div v-html="leftArrow" class="h-6 w-6 cursor-pointer"></div>
          </button>
        </div>

        <div v-if="!isDashboard">
          <button
            type="button"
            @click="goToNextPage"
            class="cursor-pointer !bg-transparent p-0 border-none"
            :aria-label="$t('accessibilityLabels.goToNextPage')"
          >
            <div
              v-html="rightArrowDisabled({ color: '#494949' })"
              class="h-6 w-6 cursor-pointer"
            ></div>
          </button>
        </div>
        <div class="w-[1px] bg-gray-300 h-6"></div>
      </div>

      <div class="flex items-center gap-4">
        <div class="flex items-center gap-4">
          <LanguageDropdown class="mr-2" />
          <div>
            <span
              class="text-softGray font-just text-base font-500 leading-normal"
              >{{ $t('layoutLabels.hello') }}
            </span>
            <span
              class="text-softGray font-just text-base font-bold leading-normal"
              >&nbsp;{{ userStore?.userFirstName || $t('layoutLabels.guest') }}</span
            >
          </div>

          <div
            class="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center text-white text-xs"
          >
            <img
              :key="userStore.imageVersion"
              :src="userStore.profileImage"
              alt="Profile"
              class="rounded-full w-full h-full object-cover"
              @error="onErrorImage"
            />
          </div>

          <div class="relative font-just">
            <button
              type="button"
              @click="toggleNotificationMenu"
              @keydown.enter="toggleNotificationMenu"
              class="cursor-pointer !bg-transparent p-0 border-none"
              :aria-label="$t('accessibilityLabels.openNotifications')"
            >
              <span
                v-html="notificationIcon({ color: '#494949' })"
                class="h-6 w-6"
              ></span>
              <span
                v-if="notificationStore.newNotificationsCount > 0"
                class="absolute -top-2 -right-2 bg-notificationBadge text-white text-[12px] font-[500] leading-[100%] tracking-[0%] rounded-full px-1.5 py-0.5 flex items-center justify-center min-w-[20px] min-h-[20px] border-2 border-white"
              >
                {{ notificationStore.newNotificationsCount }}
              </span>
            </button>
            <Menu
              ref="notificationMenu"
              :model="notificationMenuItems"
              :popup="true"
              class="notification-menu min-w-[320px] max-w-[360px]"
              :pt="{
                list: {
                  class: `scroll-thin overflow-y-auto ${
                    notificationMenuItems.length == 1 &&
                    notificationMenuItems[0]?.id === 'no-data'
                      ? 'max-h-[65px]'
                      : 'max-h-[300px]'
                  }`,
                },
                item: { class: 'gap-3 flex' },
                itemContent: { class: 'p-3 gap-3 flex' },
                separator: { class: 'block mx-3 bg-grayLight' },
                root: { class: '!rounded-b-2xl flex bg-white !gap-3' },
              }"
              style="
                box-shadow: -4px 11px 26px 0px #0000001a;
                box-shadow: -16px 43px 46px 0px #00000017;
                box-shadow: -37px 98px 63px 0px #0000000d;
                box-shadow: -65px 174px 74px 0px #00000003;
                box-shadow: -102px 272px 81px 0px #00000000;
              "
            >
              <template #item="slotProps">
                <div
                  class="cursor-pointer flex items-center justify-center gap-3"
                  @click="handleNotificationClick(slotProps.item, $event)"
                >
                  <div v-if="slotProps.item.id !== 'no-data'">
                    <span
                      v-if="slotProps.item.id % 2 !== 0"
                      class="rounded-full bg-deepSky w-8 h-8 min-w-8 min-h-8 flex items-center justify-center"
                      v-html="requestArrowSvg()"
                    ></span>
                    <span
                      v-else
                      class="rounded-full bg-greenSuccess w-8 h-8 min-w-8 min-h-8 flex items-center justify-center"
                      v-html="notificationCheckSvg()"
                    ></span>
                  </div>
                  <div
                    class="flex-1 font-just py-1 gap-2 flex items-start flex-col justify-start"
                  >
                    <div
                      class="text-btnActive text-[16px] font-semibold leading-[100%] tracking-[0%]"
                    >
                      {{ slotProps.item.label }}
                    </div>
                    <div
                      v-if="slotProps.item.id !== 'no-data'"
                      class="text-btnActive text-[14px] font-normal leading-[100%] tracking-[0%]"
                    >
                      {{ slotProps.item.description }}
                    </div>
                  </div>
                  <div v-if="slotProps.item.id !== 'no-data'">
                    <span
                      v-html="
                        rightArrow({ width: 24, height: 24, color: '#494949' })
                      "
                      class=""
                    ></span>
                  </div>
                </div>
              </template>
            </Menu>
          </div>

          <button
            ref="rightPanelToggle"
            type="button"
            @click="toggleRightPanel"
            @keydown.enter.prevent="toggleRightPanel"
            class="cursor-pointer !bg-transparent p-0 border-none focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-deepSky"
            :aria-label="$t('accessibilityLabels.toggleRightPanel')"
            :aria-expanded="rightPanelStore.isRightPanelVisible"
          >
            <div
              v-html="menuIcon"
              class="h-6 w-6 cursor-pointer pointer-events-none"
            ></div>
          </button>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { useRightPanelStore, useSidebarStore } from "@/stores/layoutStore";
import { ref, onMounted, onUnmounted, computed, watch } from "vue";
import {
  closeSvg,
  leftArrow,
  menuIcon,
  notificationCheckSvg,
  notificationIcon,
  requestArrowSvg,
  rightArrow,
  rightArrowDisabled,
  sidebarLeftToggle,
  sidebarLved,
} from "@/utils/commonSvg";
import { commonLabels, layoutLabels } from "@/utils/label";
import { useUserStore } from "@/stores/userStore";
import {
  decodeHtmlEntities,
  onErrorImage,
  revokeImageUrl,
  translateTemplateKeys,
} from "@/utils/common";
import Menu from "primevue/menu";
import { ROUTE_PATHS } from "@/router/constants";
import { useRouter, useRoute } from "vue-router";
import { useNotificationStore } from "@/stores/notificationStore";
import { useI18n } from "vue-i18n";
import LanguageDropdown from "@/components/common/LanguageDropdown.vue";

// Router & stores
const router = useRouter();
const route = useRoute();
const rightPanelStore = useRightPanelStore();
const sidebarStore = useSidebarStore();
const userStore = useUserStore();
const notificationStore = useNotificationStore();
const { t, te, locale } = useI18n();

// UI state
const isSmallScreen = ref(window.innerWidth < 768);
const isAbove426 = ref(window.innerWidth >= 426);
const showSearchInput = ref(false);
const notificationMenu = ref();

// Resolves a notification text string to its translated equivalent.
// 1. If the whole string is a valid i18n key → translate with interpolation params from content
// 2. Otherwise → replace any embedded templates.* keys via translateTemplateKeys
const resolveNotificationText = (text, params = {}) => {
  if (!text) return text;
  if (te(text)) return t(text, params);
  return translateTemplateKeys(text, te, t);
};

// Notification items list — re-evaluates on locale change
const notificationMenuItems = computed(() => {
  // Explicitly access locale.value to make this computed locale-reactive
  const _locale = locale.value; // eslint-disable-line no-unused-vars

  const items = notificationStore.notifications
    .filter((n) => n.status === "new")
    .map((notification) => ({
      id: notification.id,
      label: resolveNotificationText(notification.title, notification.content || {}),
      description: resolveNotificationText(
        decodeHtmlEntities(notification.plainBody),
        notification.content || {}
      ),
      icon:
        notification?.type === "request"
          ? requestArrowSvg()
          : notificationCheckSvg(),
    }));

  if (items.length === 0) {
    return [
      { label: t('commonLabels.noNewNotifications'), id: "no-data", disabled: true },
    ];
  }
  // Insert separator between items
  return items.flatMap((item, index) =>
    index < items.length - 1 ? [item, { separator: true }] : [item]
  );
});

// Check if current page is dashboard
const isDashboard = computed(() => {
  return route.name === "dashboard" || route.path === "/dashboard";
});

// Check if user can navigate back
const canGoBack = computed(() => {
  return window.history.length > 1;
});

// Navigation helpers
const goToPreviousPage = () => {
  router.go(-1);
};

const goToNextPage = () => {
  router.go(1);
};

// Toggle notification dropdown
function toggleNotificationMenu(event) {
  notificationMenu.value.toggle(event);
  if (!notificationStore.newNotificationsCount)
    router.push(ROUTE_PATHS.NOTIFICATIONS);
}

// Handle notification item click
function handleNotificationClick(item, event) {
  router.push(ROUTE_PATHS.NOTIFICATIONS);
}

// Resize listener for responsive layout
const handleResize = () => {
  isSmallScreen.value = window.innerWidth < 768;
  isAbove426.value = window.innerWidth >= 426;
};

// Fetch notifications from the API
const getNotifications = async (filter) => {
  try {
    await notificationStore.getNotifications(filter);
  } catch (error) {
    console.error("Error get notification:", error);
    throw error;
  }
};

// Lifecycle hooks
onMounted(async () => {
  await getNotifications();
  window.addEventListener("resize", handleResize);
  if (!userStore.profileImage && userStore.loginResponse) {
    try {
      await userStore.getProfileImage();
    } catch (error) {
      console.error("Error loading profile image:", error);
    }
  }
});

onUnmounted(() => {
  window.removeEventListener("resize", handleResize);
  if (userStore.profileImage) {
    revokeImageUrl(userStore.profileImage);
  }
});

// Toggle right side panel
const toggleRightPanel = () => {
  rightPanelStore.toggleRightPanel();
};

// Toggle search input
const toggleSearchInput = () => {
  showSearchInput.value = !showSearchInput.value;
};

watch(
  () => rightPanelStore.isRightPanelVisible,
  (isVisible) => {
    if (isVisible) {
      const mainContent = document.querySelector(
        "main, #app > div:not(.right-panel)"
      );
      if (mainContent) {
        mainContent.setAttribute("inert", "");
        mainContent.setAttribute("aria-hidden", "true");
      }
    } else {
      const mainContent = document.querySelector(
        "main, #app > div:not(.right-panel)"
      );
      if (mainContent) {
        mainContent.removeAttribute("inert");
        mainContent.removeAttribute("aria-hidden");
      }
    }
  }
);
</script>
